import React, { FC, useRef } from "react";
import ReactLightbox, { useLightboxState } from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

import { Box } from "@UI/Components/Box/Box";
import { DetailLine } from "@UI/Components/DetailLine/DetailLine";
import useI18n from "@I18n/hooks/useI18n";
import { ModeratedContentInfo } from "@modules/ServiceRequest/Components/ModeratedContentInfo";
import {
  ModeratedText,
  hasModeratedContent,
} from "@modules/ServiceRequest/Components/ModeratedText";

import messages from "../../../messages";

export interface PhotoLightboxSlide {
  src: string;
  description?: string;
}

export interface PhotoLightboxProps {
  isOpen: boolean;
  index: number;
  slides: PhotoLightboxSlide[];
  onClose: () => void;
}

/**
 * The description (when the photo has one) and the pagination dots, both
 * absent from the library itself — `yet-another-react-lightbox` ships a
 * `Captions` plugin and a `Counter` plugin, but no dot indicator, and the
 * design puts both in one block rather than in either plugin's own spot.
 *
 * Reads off `useLightboxState()` rather than props so it stays in sync with
 * swipe/keyboard navigation, not just the `index` this mounted with.
 */
const Footer: FC = () => {
  const { formatMessage } = useI18n();
  const { currentSlide, slides, currentIndex } = useLightboxState();
  const description = (currentSlide as PhotoLightboxSlide | undefined)
    ?.description;
  const hasModeration = hasModeratedContent(description);
  // The tooltip portals in here rather than to the body: the lightbox is a
  // `position: fixed` overlay, and a body-level tooltip is placed in document
  // space, landing off-screen by the scroll offset of the page behind it.
  const tooltipContainerRef = useRef<HTMLDivElement>(null);

  return (
    <Box
      position="absolute"
      left="0"
      right="0"
      bottom="24px"
      pointerEvents="none"
      paddingY="8px"
      backgroundImage="linear-gradient(0deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.2))"
    >
      {description && (
        <Box
          maxWidth="480px"
          marginX="auto"
          paddingX="24px"
          marginBottom="16px"
        >
          <DetailLine
            title={formatMessage(
              messages["taskCreation.photos.descriptionLabel"]
            )}
            detail={
              <ModeratedText
                text={description}
                color="white"
                labelColor="white"
              />
            }
            titleColor="white"
          />
          {hasModeration && (
            // The footer overlay above is `pointerEvents="none"` so it
            // doesn't block the lightbox's own controls — this button needs
            // its own opt-back-in to receive hover/click.
            <Box
              ref={tooltipContainerRef}
              pointerEvents="auto"
              display="inline-block"
              marginTop="8px"
            >
              <ModeratedContentInfo portalContainerRef={tooltipContainerRef} />
            </Box>
          )}
        </Box>
      )}
      <Box display="flex" justifyContent="center" gap="8px">
        {slides.map((_, slideIndex) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={slideIndex}
            aria-hidden
            boxSize={slideIndex === currentIndex ? "8px" : "6px"}
            borderRadius="full"
            background={
              slideIndex === currentIndex ? "white" : "whiteAlpha.500"
            }
            transition="all 0.2s"
          />
        ))}
      </Box>
    </Box>
  );
};

/** Click-to-enlarge viewer for a draft's photos, with swipe/arrow navigation
 * between them. `slides` and `index` are owned by the caller (`PhotoUploader`)
 * since it's the one holding the full photo list. */
export const PhotoLightbox: FC<PhotoLightboxProps> = ({
  isOpen,
  index,
  slides,
  onClose,
}) => (
  <ReactLightbox
    open={isOpen}
    close={onClose}
    index={index}
    slides={slides}
    render={{ controls: () => <Footer /> }}
  />
);
